import React, { useState } from 'react';
import { Typography, Card, Form, Input, Button, message, Alert } from 'antd';
import { LockOutlined, SafetyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import axios from 'axios';

const { Title, Paragraph, Text } = Typography;

const DeleteAccount = () => {
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [needSms, setNeedSms] = useState(false);
  const [form] = Form.useForm();
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  React.useEffect(() => {
    const checkAuthMethod = async () => {
      try {
        const res = await axios.get('/auth/verify');
        if (res.data.success && res.data.user) {
          setNeedSms(!res.data.user.has_password);
        }
      } catch (_) {
        // 未登录时由路由守卫处理
      }
    };
    checkAuthMethod();
  }, []);

  const handleSendCode = async () => {
    if (!user?.phone) {
      message.error('当前账号未绑定手机号，请发送邮件至 support@manage.net.cn 申请注销');
      return;
    }
    setSendingCode(true);
    try {
      const res = await axios.post('/auth/sms/send-code', {
        phone: user.phone,
        purpose: 'delete_account',
      });
      if (res.data.success) {
        message.success('验证码已发送');
        setCountdown(60);
        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        message.error(res.data.message || '发送失败');
      }
    } catch (error) {
      message.error('发送验证码失败');
    } finally {
      setSendingCode(false);
    }
  };

  const handleDelete = async (values) => {
    setLoading(true);
    try {
      const payload = {
        confirm: values.confirm,
        password: values.password,
        sms_code: values.sms_code,
      };
      const res = await axios.post('/account/delete', payload);
      if (res.data.success) {
        message.success(res.data.message || '账号已注销');
        await logout();
        navigate('/');
      } else {
        message.error(res.data.message || '注销失败');
      }
    } catch (error) {
      message.error(error.response?.data?.message || '注销失败，请检查信息后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 560, margin: '20px auto', padding: '0 16px' }}>
      <Card>
        <Title level={3} style={{ color: '#cf1322' }}>账号注销</Title>
        <Paragraph type="secondary">
          注销后您的账号及个人数据（文件、物品、财务记录等）将被删除且无法恢复。法律法规要求保留的信息除外。
        </Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 13 }}>
          注销前请阅读
          <a href="/privacy" target="_blank" rel="noopener noreferrer">《隐私政策》</a>
          中「账号注销规则」章节。
        </Paragraph>

        <Alert
          type="warning"
          showIcon
          message="此操作不可恢复，请谨慎操作"
          style={{ marginBottom: 20 }}
        />

        <Form form={form} layout="vertical" onFinish={handleDelete}>
          {!needSms ? (
            <Form.Item
              name="password"
              label="登录密码"
              rules={[{ required: true, message: '请输入登录密码' }]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="请输入当前登录密码" />
            </Form.Item>
          ) : (
            <Form.Item
              name="sms_code"
              label="短信验证码"
              rules={[{ required: true, message: '请输入短信验证码' }]}
              extra={<Text type="secondary">微信登录账号需通过短信验证身份</Text>}
            >
              <Input
                prefix={<SafetyOutlined />}
                placeholder="请输入验证码"
                addonAfter={
                  <Button
                    type="link"
                    size="small"
                    disabled={countdown > 0 || sendingCode}
                    onClick={handleSendCode}
                    style={{ padding: 0 }}
                  >
                    {countdown > 0 ? `${countdown}秒` : '获取验证码'}
                  </Button>
                }
              />
            </Form.Item>
          )}

          <Form.Item
            name="confirm"
            label={<>确认注销（请输入 <Text strong>DELETE</Text>）</>}
            rules={[
              { required: true, message: '请输入 DELETE 确认' },
              { pattern: /^DELETE$/, message: '请准确输入 DELETE' },
            ]}
          >
            <Input placeholder="DELETE" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" danger htmlType="submit" loading={loading} block>
              确认注销账号
            </Button>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'center' }}>
            <Button type="link" onClick={() => navigate('/profile')}>
              返回个人中心
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default DeleteAccount;
